import type {
  PropertyDefinition,
  PropertyValidationErrors,
  PropertyValue,
  PropertyValuesRecord,
  ValueType,
} from '@github-ui/custom-properties-types'
import {isPropertyValueArray} from '@github-ui/custom-properties-types/helpers'
import {AlertFillIcon, CheckboxIcon, type Icon, SingleSelectIcon, TypographyIcon} from '@primer/octicons-react'
import {Box, FormControl, Text} from '@primer/react'

import {EmptyState} from './EmptyState'

interface Props {
  definitions: PropertyDefinition[]
  properties: PropertyValuesRecord
  errors: PropertyValidationErrors
}

const propertyNotDefinedMessage = 'Property is not defined'

const borderStyle = 'solid 1px var(--borderColor-default, var(--color-border-default))'

export function PropertiesList(props: Props) {
  const {definitions, properties, errors} = props

  const nonDefinedProperties = Object.keys(properties).filter(key => !definitions.some(d => d.propertyName === key))
  const missingDefinitions: PropertyDefinition[] = nonDefinedProperties.map(propertyName => ({
    propertyName,
    valueType: 'string',
    description: null,
    required: false,
    defaultValue: null,
    allowedValues: null,
    valuesEditableBy: 'org_actors',
  }))

  const allDefinitions = [...definitions, ...missingDefinitions]

  if (allDefinitions.length === 0) {
    return <EmptyState>No custom properties defined for this organization.</EmptyState>
  }

  function isUnknownProperty(definition: PropertyDefinition) {
    return nonDefinedProperties.includes(definition.propertyName)
  }

  return (
    <Box
      as="ul"
      sx={{
        border: borderStyle,
        borderRadius: '5px',
        boxSizing: 'border-box',
        minWidth: '100%',
        textAlign: 'left',
        listStyleType: 'none',
        li: {
          borderBottom: borderStyle,
        },
        'li:last-child': {
          borderColor: 'transparent',
        },
      }}
      data-hpc
    >
      {allDefinitions.map(definition => {
        const {propertyName} = definition
        const isUnknown = isUnknownProperty(definition)
        const propertyValue = properties[propertyName] || ''

        let errorMessage = errors[propertyName]
        if (isUnknown && propertyValue !== '') {
          errorMessage = propertyNotDefinedMessage
        } else if (propertyValue === '') {
          errorMessage = undefined
        }

        return (
          <PropertyRow
            definition={definition}
            key={propertyName}
            value={propertyValue}
            error={errorMessage}
            isUnknownProperty={isUnknown}
          />
        )
      })}
    </Box>
  )
}

function PropertyRow({
  definition,
  value,
  error,
  isUnknownProperty,
}: {
  definition: PropertyDefinition
  value: PropertyValue
  error: string | undefined
  isUnknownProperty: boolean
}) {
  const {propertyName, description} = definition

  return (
    <Box
      as="li"
      sx={{
        p: 2,
        display: 'flex',
      }}
    >
      <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
          <Box>
            <PropertyName definition={definition} isUnknownProperty={isUnknownProperty} />
            <Box sx={{ml: 4}}>{description && <Box sx={{color: 'fg.muted', fontSize: 0}}>{description}</Box>}</Box>
          </Box>

          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <FormControl
              sx={{
                width: '200px',
                alignItems: 'flex-end',
              }}
            >
              <FormControl.Label visuallyHidden>{propertyName}</FormControl.Label>
              <Text
                data-testid={`${propertyName}-value`}
                sx={{
                  fontSize: 'small',
                  color: 'fg.muted',
                  wordBreak: 'break-all',
                }}
              >
                {isPropertyValueArray(value) ? value.join(', ') : value}
              </Text>
              {error && <FormControl.Validation variant="error">{error}</FormControl.Validation>}
            </FormControl>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const propertyIcon: Record<ValueType, Icon> = {
  ['single_select']: SingleSelectIcon,
  ['multi_select']: SingleSelectIcon,
  string: TypographyIcon,
  boolean: CheckboxIcon,
}

function PropertyName({definition, isUnknownProperty}: {definition: PropertyDefinition; isUnknownProperty: boolean}) {
  const PropertyIcon = propertyIcon[definition.valueType]
  return (
    <Box sx={{display: 'flex', gap: 2}}>
      {isUnknownProperty ? (
        <Box sx={{color: 'danger.fg'}}>
          <AlertFillIcon size={14} />
        </Box>
      ) : (
        <Box sx={{color: 'fg.muted'}}>
          <PropertyIcon size={14} />
        </Box>
      )}
      <Box sx={{fontWeight: 'bold', wordBreak: 'break-all'}}>{definition.propertyName}</Box>
    </Box>
  )
}

try{ PropertiesList.displayName ||= 'PropertiesList' } catch {}
try{ PropertyRow.displayName ||= 'PropertyRow' } catch {}
try{ PropertyName.displayName ||= 'PropertyName' } catch {}